/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductAttributes, { ProductAttributesStyles } from "@stoneage/Overrides/Components/ProductAttributes";
import ProductDescription, { ProductDescriptionStyles } from "@stoneage/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@stoneage/Overrides/Components/ProductPartNumbers";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    extendedStyles?: ProductListProductInformationStyles;

    view: "List" | "Grid";
    productDescriptionClickHandler?: (...args: any[]) => void;
}

type Props = OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: InjectableCss;
    descriptionStyles?: ProductDescriptionStyles;
    descriptionWrapper?: InjectableCss;
    swatchWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    availabilityWrapper?: InjectableCss;
    productAttributes?: ProductAttributesStyles;
    searchScoreDataWrapper?: InjectableCss;
    partNumbersWrapper?: InjectableCss;
    attributesWrapper?: InjectableCss;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            flex: 1;
            max-width: ${({ view }: any) => (view === "List" ? "calc(100% - 284px)" : "unset")};
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            color: "common.black",
            typographyProps: {
                size: 15,
            },
            css: css`
                width: 100%;
            `,
        },
    },

    descriptionWrapper: {
        css: css`
            height: 38px;
            overflow: hidden;
            line-height: 19px;
            margin-bottom: 12px;
            ${xScreen("lte", "md")} {
                height: auto;
            }
        `,
    },
    productAttributes: {
        attributeTypeWrapper: {
            css: css`
                overflow: hidden;
                text-overflow: ellipsis;
            `,
        },
        attributeValueWrapper: {
            css: css`
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 180px;
            `,
        },
        attributeTypeText: {
            size: 12,
            color: "text.main",
            css: css`
                white-space: nowrap;
            `,
        },
        attributeValueText: {
            weight: "bold",
            size: 12,
            color: "text.main",
            css: css`
                white-space: nowrap;
                text-overflow: ellipsis;
            `,
        },
    },
};

const ProductListProductInformation: FC<Props> = ({
    productContext,
    showTitle,
    showPartNumbers,
    showAttributes,
    extendedStyles,
    view,
    productDescriptionClickHandler,
}) => {
    const [styles] = React.useState(() => mergeToNew(productInformationStyles, extendedStyles));
    const { product } = productContext;
    let variantDisplayTrait = null;

    if (!product) {
        return null;
    }
    if (product?.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <StyledWrapper {...styles.container} {...({ view } as any)}>
            {showTitle && (
                <StyledWrapper {...styles.descriptionWrapper}>
                    <ProductDescription
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                        lines={2}
                        lineHeight={19}
                        onClick={() => productDescriptionClickHandler?.(productContext?.product.productNumber)}
                    />
                </StyledWrapper>
            )}
            {showAttributes && (
                <StyledWrapper {...styles.attributesWrapper} {...({ view } as any)}>
                    <ProductAttributes
                        maximumNumberAttributeTypes={3}
                        extendedStyles={styles.productAttributes}
                        {...({ view } as any)}
                    />
                </StyledWrapper>
            )}
            {showPartNumbers && (
                <StyledWrapper {...styles.partNumbersWrapper}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showManufacturerItem={false}
                    />
                </StyledWrapper>
            )}
            {variantDisplayTrait && (
                <StyledWrapper {...styles.swatchWrapper}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(ProductListProductInformation);
